import { useStores } from '@littlebird/sdk';
import { deviceCount } from '@littlebird/ui-utils';
import { useCallback } from 'react';

import { electronApis } from '@/features/electron-api';
import { logError } from '@/lib/errorLogging';

import { chooseReportArtifact } from './report-body';
import { useNotifyRoutineReport } from './routine-report-notify';
import { formatNoteDate } from './routines.utils';

export const getRoutineReportNotificationId = (reportId: number): string =>
  `routine-report-${reportId}`;

export const useRoutineReportNotifier = () => {
  const { files: filesStore, reports: reportStore } = useStores();
  const notifyRoutineReport = useNotifyRoutineReport();

  const notifyFromPush = useCallback(
    async (rawReportId: string, signal?: AbortSignal): Promise<boolean> => {
      const reportId = Number(rawReportId);
      if (!Number.isSafeInteger(reportId) || reportId <= 0 || signal?.aborted) {
        return false;
      }

      const notificationId = getRoutineReportNotificationId(reportId);

      try {
        const alreadyTracked =
          await electronApis.lbNotifications.hasNotification(notificationId);
        if (alreadyTracked) return true;
        if (signal?.aborted) return false;

        const report = await reportStore.fetchByReferenceId(reportId);
        if (!report) {
          logError(
            new Error('Routine report fetch returned nothing'),
            'Failed to prepare a routine report notification',
            { context: 'useRoutineReportNotifier', metadata: { reportId } }
          );
          return false;
        }
        const artifactFileIds = report.artifactFileIds;
        if (artifactFileIds.length === 0 || signal?.aborted) return false;

        await filesStore.hydrateByIds(artifactFileIds);
        if (signal?.aborted) return false;

        const choice = chooseReportArtifact(
          artifactFileIds.map((fileId) => filesStore.getById(fileId))
        );
        if (!choice?.isCanvas || !choice.file) return false;

        return notifyRoutineReport({
          artifact: choice.file,
          title: report.title,
          subtitle:
            report.ranWithout.length > 0
              ? `Skipped ${deviceCount(report.ranWithout.length)}`
              : formatNoteDate(report.scheduledAt),
          route: {
            to: `/reports/${report.reportTemplateId}`,
            search: { reportId },
          },
          notificationId,
          signal,
        });
      } catch (error) {
        if (signal?.aborted) return false;
        logError(error, 'Failed to prepare a routine report notification', {
          context: 'useRoutineReportNotifier',
          metadata: { reportId },
        });
        return false;
      }
    },
    [filesStore, reportStore, notifyRoutineReport]
  );

  return { notifyFromPush };
};
